import type { JSX } from 'react'
import { CircleCheck, TriangleAlert } from 'lucide-react'
import type { AlertTestResultDto } from '@/lib/dto'
import { cn } from '@/lib/cn'

/**
 * Итог «Проверить доставку».
 *
 * Живёт рядом с формой, а не в тосте: сервер отвечает 200 и при неудаче
 * (`{ delivered:false, error:'HTTP 404' }`) — это результат проверки, ради
 * которого кнопку и нажимали. Тост уехал бы через четыре секунды, а причина
 * нужна ровно в тот момент, когда правят адрес.
 *
 * `role="status"`, а не `alert`: провал доставки — не ошибка страницы, но
 * озвучить его скринридеру обязательно, иначе нажатие кнопки выглядит как
 * «ничего не произошло».
 */
export function AlertTestResult({ result }: { result: AlertTestResultDto }): JSX.Element {
  const ok = result.delivered
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2.5 rounded-sm border px-3 py-2.5',
        ok ? 'border-live/40 bg-live/12' : 'border-warn/40 bg-warn/12'
      )}
    >
      <span aria-hidden className={cn('mt-0.5 shrink-0', ok ? 'text-live' : 'text-warn')}>
        {ok ? <CircleCheck className="size-4" /> : <TriangleAlert className="size-4" />}
      </span>
      <div className="min-w-0">
        <p className={cn('text-base', ok ? 'text-live' : 'text-warn')}>
          {ok ? 'Пробное сообщение доставлено' : 'Доставить пробное сообщение не удалось'}
        </p>
        {ok ? (
          <p className="mt-0.5 max-w-prose text-2xs text-ink-muted">
            Проверьте чат: сообщение уже там. Значит, настоящий алерт тоже дойдёт.
          </p>
        ) : (
          <p className="mt-0.5 max-w-prose font-mono text-2xs break-words text-ink-muted">
            {result.error ?? 'Транспорт не сообщил причину'}
          </p>
        )}
      </div>
    </div>
  )
}
